import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelTitle } from 'components/layout/Panel'
import Page from 'rui/widgets/Page'
import { Faq, Container, Button, Text, Spacer } from 'roadie-ui'
import { getScorecardByMetricName, RETURN_RATE_METRIC_NAME, Scorecard } from 'util/scorecard'
import { useDispatch, useSelector } from 'react-redux'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import { getScorecardStatusKey } from 'util/scorecard'
import { ReturnRateIcon } from './ReturnRateIcon'
import { ReturnRateMessage } from './ReturnRateMessage'
import React from 'react'
import { DrivingProfile } from 'types/profile'
import { FeatureFlag } from 'Optimizely'
import { Redirect } from 'react-router-dom'
import { fetchDriverScorecardSummary } from 'actions/scorecard'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

const formatUpdatedAt = (updatedAt: Date | string | null | undefined, locale: string) => {
  if (!updatedAt) return ''

  const date = new Date(updatedAt)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString(locale, { month: 'long', day: 'numeric' })
}

const ReturnRateContent: React.FC = () => {
  const { t, locale } = useLocalization()
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const scorecardSummary = useSelector((state: { scorecardSummary: Scorecard[] }) => state.scorecardSummary)
  const returnRateScorecard = getScorecardByMetricName(scorecardSummary, RETURN_RATE_METRIC_NAME)

  const dispatch = useDispatch()
  React.useEffect(() => {
    fetchDriverScorecardSummary(profile.id)(dispatch)
  }, [dispatch, profile.id])

  if (!returnRateScorecard?.scorecardBand) {
    return null
  }

  const updatedAtFormatted = formatUpdatedAt(returnRateScorecard?.updatedAt, locale)
  const scorecardStatusKey = returnRateScorecard?.scorecardBand
    ? getScorecardStatusKey(returnRateScorecard.scorecardBand)
    : null

  return (
    <Page sidebar={<DriveSidebar />}>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_my_profile'), linkTo: '/dashboard' },
            { label: t('dweb_common_return_rate') },
          ]}
        />
      )}
      <Panel noPadding>
        <Container.Base $backgroundColor="primary-dark" $padding="2.4rem">
          <Text.Body2 $color="neutral-white">
            <LocalizedText translationKey="dweb_dashboard_return_rate_title_with_colon" />
          </Text.Body2>
          <Container.Grid
            $gridTemplateColumns="auto auto"
            $gridGap="0.8rem"
            $justifyContent="center"
            $alignItems="center"
            $padding="0.8rem 0"
          >
            <ReturnRateIcon scorecardBand={returnRateScorecard.scorecardBand} />
            <Text.H5 $fontWeight="bold" $color="neutral-white" $whiteSpace="nowrap">
              {scorecardStatusKey ? <LocalizedText translationKey={scorecardStatusKey} /> : null}
            </Text.H5>
          </Container.Grid>
          <Text.Caption>
            <LocalizedRichText
              translationKey="dweb_dashboard_return_rate_last_updated_value1"
              params={{ value1: updatedAtFormatted }}
            />
          </Text.Caption>
          <Text.Caption>
            <LocalizedText translationKey="dweb_dashboard_return_rate_updates_every_2_weeks" />
          </Text.Caption>
        </Container.Base>
      </Panel>
      <Panel left>
        <ReturnRateMessage scorecardBand={returnRateScorecard.scorecardBand} />
        <Container.Base $padding="2.4rem 0 0 0" $textAlign="center">
          <Button.A.Tertiary href={`${import.meta.env.REACT_APP_SUPPORT_URL}`}>
            <LocalizedText translationKey="dweb_common_learn_more" />
          </Button.A.Tertiary>
        </Container.Base>
      </Panel>
      <Spacer $height="1.6rem" />
      <PanelTitle>
        <LocalizedText translationKey="dweb_dashboard_account_status_faqs" />
      </PanelTitle>
      <Panel noPadding>
        <Container.Base $padding="1.6rem 2.4rem">
          <Faq title={t('dweb_dashboard_return_rate_how_return_rate_measured')}>
            <LocalizedText translationKey="dweb_dashboard_return_rate_faq_how_measured_body" />
          </Faq>
          <Spacer $height="2.4rem" />
          {returnRateScorecard.scorecardBand !== 'acceptable' && (
            <>
              <Faq title={t('dweb_dashboard_return_rate_how_will_i_know_if_my_return_rate')}>
                <LocalizedText translationKey="dweb_dashboard_return_rate_faq_reviewed_every_two_weeks_body" />
              </Faq>
              <Spacer $height="2.4rem" />
            </>
          )}
          <Faq title={t('dweb_dashboard_return_rate_what_some_tips_i_can_use_improve_my')}>
            <ul>
              <li>
                <LocalizedRichText
                  translationKey="dweb_dashboard_return_rate_strong_text1_if_contact_details_provided_touch_base"
                  params={{
                    strongText1: (
                      <strong>
                        <LocalizedText translationKey="dweb_dashboard_return_rate_reach_out_recipients" />
                      </strong>
                    ),
                  }}
                />
              </li>
              <li>
                <LocalizedRichText
                  translationKey="dweb_dashboard_return_rate_strong_text1_sure_wait_full_time_required_by"
                  params={{
                    strongText1: (
                      <strong>
                        <LocalizedText translationKey="dweb_dashboard_return_rate_follow_wait_time_policy" />
                      </strong>
                    ),
                  }}
                />
              </li>
              <li>
                <LocalizedRichText
                  translationKey="dweb_dashboard_return_rate_strong_text1_review_delivery_notes_again_any_special"
                  params={{
                    strongText1: (
                      <strong>
                        <LocalizedText translationKey="dweb_dashboard_return_rate_double_check_before_returning" />
                      </strong>
                    ),
                  }}
                />
              </li>
              <li>
                <LocalizedRichText
                  translationKey="dweb_dashboard_return_rate_strong_text1_always_rings_doorbell_knock_door_before"
                  params={{
                    strongText1: (
                      <strong>
                        <LocalizedText translationKey="dweb_dashboard_return_rate_knock_ring" />
                      </strong>
                    ),
                  }}
                />
              </li>
            </ul>
          </Faq>
        </Container.Base>
      </Panel>
    </Page>
  )
}

const ReturnRatePage: React.FC = () => (
  <FeatureFlag feature="driver_scorecard">
    {(enabled) => (enabled ? <ReturnRateContent /> : <Redirect to="/dashboard" />)}
  </FeatureFlag>
)

export default ReturnRatePage
